// Add DataPills component to display additionalData keys
import React, { useState, useRef, useEffect } from "react";
import hljs from "highlight.js";
import "highlight.js/styles/a11y-dark.css";
import styles from "./Composer.module.css";

export function DataPills({ data }: { data?: Record<string, any> }) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const pillRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const tooltipTimeoutRef = useRef<number | null>(null);

  // Format and highlight data value for display in tooltip
  const formatValue = (value: any): string => {
    if (value === null) return "null";
    if (value === undefined) return "undefined";
    if (typeof value === "object") {
      try {
        const jsonString = JSON.stringify(value, null, 2);
        return hljs.highlight(jsonString, { language: "json" }).value;
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  // Calculate tooltip position based on pill position
  useEffect(() => {
    if (hoveredKey && pillRefs.current[hoveredKey]) {
      const pillElement = pillRefs.current[hoveredKey];
      if (!pillElement) return;

      const pillRect = pillElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const tooltipWidth = Math.min(400, Math.max(200, pillRect.width * 2));

      // Calculate left position to avoid being cut off at the edge
      let leftPos = "50%";
      let transform = "translateX(-50%)";

      // If tooltip would overflow right edge of viewport
      if (pillRect.left + tooltipWidth / 2 > viewportWidth) {
        leftPos = `${Math.min(100, viewportWidth - pillRect.left - 20)}%`;
        transform = `translateX(-${leftPos})`;
      }
      // If tooltip would overflow left edge of viewport
      else if (pillRect.left - tooltipWidth / 2 < 0) {
        leftPos = `${Math.max(0, pillRect.left)}px`;
        transform = "translateX(0)";
      }

      setTooltipStyle({
        left: leftPos,
        transform,
        width: `${tooltipWidth}px`,
      });
    }
  }, [hoveredKey]);

  const handlePillMouseEnter = (key: string) => {
    if (tooltipTimeoutRef.current !== null) {
      window.clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setHoveredKey(key);
  };

  const handlePillMouseLeave = () => {
    if (tooltipTimeoutRef.current !== null) {
      window.clearTimeout(tooltipTimeoutRef.current);
    }
    tooltipTimeoutRef.current = window.setTimeout(() => {
      if (!isHoveringTooltip) {
        setHoveredKey(null);
      }
    }, 200); // Short delay to allow moving to tooltip
  };

  const handleTooltipMouseEnter = () => {
    setIsHoveringTooltip(true);
    if (tooltipTimeoutRef.current !== null) {
      window.clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
  };

  const handleTooltipMouseLeave = () => {
    setIsHoveringTooltip(false);
    setHoveredKey(null);
  };

  if (!data || Object.keys(data).length === 0) return null;

  return (
    <div className={styles.dataPills}>
      {Object.keys(data).map((key) => (
        <div key={key} className={styles.pillContainer}>
          <span
            ref={(el) => {
              pillRefs.current[key] = el;
            }}
            className={styles.dataPill}
            onMouseEnter={() => handlePillMouseEnter(key)}
            onMouseLeave={handlePillMouseLeave}
          >
            {key}
          </span>
          {hoveredKey === key && (
            <div
              ref={tooltipRef}
              className={styles.pillTooltip}
              style={tooltipStyle}
              onMouseEnter={handleTooltipMouseEnter}
              onMouseLeave={handleTooltipMouseLeave}
            >
              <div className={styles.tooltipContent}>
                <pre className={styles.codeBlock}>
                  <code
                    className="hljs language-json"
                    dangerouslySetInnerHTML={{
                      __html: formatValue(data[key]),
                    }}
                  />
                </pre>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
